import { format as d3Format } from 'd3-format';
/**
 * Renders raw Cube query values using the Cube `format` annotation (SSP-3972).
 *
 * Cube stores `format` as data-model metadata only — the load API returns RAW values, so the UI must
 * interpret the format itself. The format vocabulary mirrors cube-dev/data-models/format-utils.js:
 *   - percent_N / number_N / currency_N / decimal_N / accounting_N -> d3-format specifiers
 *   - abbr_N -> Intl compact notation (K/M/B/T, matching Cube's output — d3's SI prefixes give k/G instead)
 *   - link / imageUrl -> not numeric; returned unchanged (callers render these as anchors/images)
 * Any other string is treated as a raw d3-format specifier, falling back to the raw value on error.
 */
const NAMED_FORMAT_REGEX = /^(number|percent|currency|decimal|accounting)(?:_([0-6]))?$/;
const ABBR_FORMAT_REGEX = /^abbr(?:_([0-6]))?$/;
const DEFAULT_DECIMALS = 2;
const DEFAULT_ABBR_DECIMALS = 1;
const D3_SPECIFIER_BY_NAME = {
    number: (decimals) => `,.${decimals}f`,
    decimal: (decimals) => `.${decimals}f`,
    percent: (decimals) => `.${decimals}%`,
    currency: (decimals) => `$,.${decimals}f`,
    accounting: (decimals) => `(,.${decimals}f`,
};
/**
 * The Cube load annotation may carry `format` as a plain string ('percent_2') OR as an object that
 * Cube derives at schema-compile time, e.g. `{ type: 'custom-numeric', value: '.2~%', alias: 'percent_2' }`
 * (and `{ type: 'link', label }` for links). This pulls out a usable specifier: the named `alias`
 * (preferred, so it matches our fixed-decimal intent) or the raw d3 `value` it falls back to.
 */
const extractFormatSpec = (format) => {
    if (typeof format === 'string') {
        return format || undefined;
    }
    if (typeof format === 'object' && format !== null) {
        const { alias, value } = format;
        if (typeof alias === 'string' && alias) {
            return alias;
        }
        if (typeof value === 'string' && value) {
            return value;
        }
    }
    return undefined;
};
const isFormatOfType = (format, expectedType) => typeof format === 'object' && format !== null && format.type === expectedType;
/**
 * Cube formats that are hyperlinks / images rather than numeric formatting. Each may be a plain string
 * ('link' / 'imageUrl') or an object form (`{ type: 'link', label }`), so accept both.
 */
export const isLinkFormat = (format) => format === 'link' || isFormatOfType(format, 'link');
/** Default display text for link cells when the format carries no explicit label. */
export const DEFAULT_LINK_LABEL = 'Link';
/**
 * Display text for a link cell. The whole column shares one label: the format's `label` when Cube
 * sends the object form (`{ type: 'link', label }`), otherwise the constant 'Link'. The raw URL is
 * never shown as the visible text — it is used only as the anchor href.
 */
export const getLinkLabel = (format) => {
    if (typeof format === 'object' && format !== null) {
        const { label } = format;
        if (typeof label === 'string' && label) {
            return label;
        }
    }
    return DEFAULT_LINK_LABEL;
};
/**
 * Absolute URL / non-http scheme (http(s)://, ftp://, protocol-relative //, mailto:, tel:). A value
 * matching this is already resolvable as-is; anything else is a bare host/path like
 * 'www.google.com/search' that a browser would treat as a RELATIVE path (resolved against the current
 * app URL) rather than an external link.
 */
const ABSOLUTE_URL_REGEX = /^([a-z][a-z0-9+.-]*:\/\/|\/\/|mailto:|tel:)/i;
/**
 * Normalizes a raw Cube link value into an href safe to use on an anchor. Cube stores the raw string,
 * which frequently omits the scheme (e.g. 'www.google.com/search'); left as-is the browser resolves it
 * against the current page. Prefix 'https://' unless the value already carries a scheme.
 */
export const toLinkHref = (value) => {
    const raw = value === null || value === undefined ? '' : String(value).trim();
    if (!raw || ABSOLUTE_URL_REGEX.test(raw)) {
        return raw;
    }
    return `https://${raw}`;
};
export const isImageFormat = (format) => format === 'imageUrl' || isFormatOfType(format, 'imageUrl');
/** True when the format implies numeric value formatting (i.e. has a numeric spec and isn't a link/image). */
export const isNumericCubeFormat = (format) => !isLinkFormat(format) && !isImageFormat(format) && extractFormatSpec(format) !== undefined;
/**
 * Formats a single value for display according to a Cube `format`. Returns the value coerced to a
 * string unchanged when there is no numeric format, the value is non-numeric, or formatting fails.
 * `format` is typed loosely because Cube annotations can carry non-string values (e.g. a link object).
 */
export const formatCubeValue = (value, format, locale = 'en-US') => {
    const asString = value === null || value === undefined ? '' : String(value);
    if (!isNumericCubeFormat(format)) {
        return asString;
    }
    // Nullish / blank values must not coerce to 0 (Number(null) === 0); leave them untouched.
    if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
        return asString;
    }
    const numeric = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numeric)) {
        return asString;
    }
    // Guaranteed defined by isNumericCubeFormat above (named alias or raw d3 specifier).
    const spec = extractFormatSpec(format);
    const abbrMatch = spec.match(ABBR_FORMAT_REGEX);
    if (abbrMatch) {
        const decimals = abbrMatch[1] === undefined ? DEFAULT_ABBR_DECIMALS : Number(abbrMatch[1]);
        return new Intl.NumberFormat(locale, {
            notation: 'compact',
            maximumFractionDigits: decimals,
        }).format(numeric);
    }
    const namedMatch = spec.match(NAMED_FORMAT_REGEX);
    if (namedMatch) {
        const decimals = namedMatch[2] === undefined ? DEFAULT_DECIMALS : Number(namedMatch[2]);
        return d3Format(D3_SPECIFIER_BY_NAME[namedMatch[1]](decimals))(numeric);
    }
    if (spec === 'id') {
        return d3Format('d')(numeric);
    }
    // Treat anything else as a raw d3-format specifier (e.g. ",.2f", ".1%", "$,.0f", ".3s").
    try {
        return d3Format(spec)(numeric);
    }
    catch {
        return asString;
    }
};
